import useSWR from 'swr'
import api from '@api/api'
import { useState } from 'react'
import { IObjectKey } from '@components/Atoms/Pixabay/Pixabay'
import { fetcher } from '@api/swr'
import { ThemeLayout } from '@utils/themeLayout'

export type ThemeFromEditor = {
  id: string
  theme_id: string
  project_id: any
  area_id: string
  subject_id: any
  unit_id: any
  created_at: string
  updated_at: string
  name: string
  thumbnail: string
  config: {
    palette: {
      primary: string
      secondary: string
      accent: string
      titleColor?: string
      subtitleColor?: string
      subtitle1Color?: string
      subtitle2Color?: string
      subtitle3Color?: string
    }
    topicNavigation?: boolean
    minimumReadingTimeSeconds?: number
    variant?: ThemeLayout
    colors: {
      primary: string
      secondary: string
      highlight1: string
      highlight2: string
      overlay: string
      title: string
      paragraph: string
      link: string
      background: string
      gradient: {
        default: string
      }
    }
    header: {
      showLogo: boolean
      url: string
      bg: string
    }
    footer: {
      show: boolean
      showLogo: boolean
      text: string
    }
    cover: {
      tagLabel?: string
      badgeLabel?: string
      logoUrl?: string
      textColor: string
      bg: string
      bgUrl: string
      showSubtitle: boolean
      showHeading: boolean
      coverStyle?: string
    }
    blockSpace: string
    borderRadius?: 'round' | 'sharp'
    fonts: {
      primary: string
      secondary: string
    }
  }
  resources: Array<any>
}

export interface Theme {
  id: string
  name: string
  default: boolean
  primaryColor?: string
  resources: Array<IObjectKey>
  thumbnail: string
  config: IObjectKey
  created_at: string
  updated_at: string
  available: boolean
}

interface ThemeCreate {
  name: string
  resources: Array<unknown>
  thumbnail: string
  config: unknown
}

interface ApiError extends Error {
  status?: number
  response?: {
    data?: any
    status: number
  }
}

// Hook to get an array of themes
export const useThemes = (enabled = true) => {
  const { data, error, mutate } = useSWR<{ data: Theme[] }>(
    enabled ? '/themes' : null,
    enabled ? api.get : null
  )

  return {
    data: data?.data || [],
    isLoading: enabled && !data && !error,
    error,
    mutate
  }
}

// Hook to get a single theme by id
export const useThemeById = (id: string) => {
  const { data, error, mutate } = useSWR<Theme>(
    id ? `/themes/${id}` : null,
    api.get
  )

  return {
    data,
    isLoading: !data && !error,
    error,
    mutate
  }
}

// Hook to create a new theme
export const useCreateTheme = () => {
  const [error, setError] = useState<ApiError | null>(null)
  const createTheme = async (data: ThemeCreate) => {
    try {
      const response = await api.post('/themes', data)
      setError(null)
      return response
    } catch (err) {
      const apiError = err as ApiError
      setError(apiError)
      throw apiError
    }
  }

  return { createTheme, error }
}

// Hook to apply a theme to an entity
export const useApplyTheme = () => {
  const [error, setError] = useState<ApiError | null>(null)
  const applyTheme = async (themeId: string, entityId: string) => {
    try {
      const response = await api.post(`/themes/${themeId}/apply/${entityId}`)
      setError(null)
      return response
    } catch (err) {
      const apiError = err as ApiError
      setError(apiError)
      throw apiError
    }
  }

  return { applyTheme, error }
}

// Hook to update an existing theme by id
export const useUpdateTheme = () => {
  const [error, setError] = useState<ApiError | null>(null)
  const updateTheme = async (id: string, data: Partial<Theme>) => {
    try {
      const response = await api.put(`/themes/${id}`, data)
      setError(null)
      return response
    } catch (err) {
      const apiError = err as ApiError
      setError(apiError)
      throw apiError
    }
  }

  return { updateTheme, error }
}

// Hook to delete a theme by id
export const useDeleteTheme = () => {
  const [error, setError] = useState<ApiError | null>(null)
  const deleteTheme = async (id: string) => {
    try {
      const response = await api.delete(`/themes/${id}`)
      setError(null)
      return response
    } catch (err) {
      const apiError = err as ApiError
      setError(apiError)
      throw apiError
    }
  }

  return { deleteTheme, error }
}
